import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, ArrowLeft, BookOpen, Users, Shield, Globe, Eye, EyeOff } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { language, toggleLanguage, t, localize } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'student',
    accepted_terms: false,
    preferred_language: language,
    interested_in_special_needs: false,   // optional autism-path opt-in (parents)
  });

  useEffect(() => {
    setFormData(prev => ({ ...prev, preferred_language: language }));
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accepted_terms) {
      toast.error(t('mustAcceptTerms'));
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(localize({ ar: 'كلمات المرور غير متطابقة', en: 'Passwords do not match' }));
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(localize({ ar: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل', en: 'Password must be at least 6 characters' }));
      return;
    }
    
    setLoading(true);
    
    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role,
        accepted_terms: formData.accepted_terms,
        preferred_language: formData.preferred_language,
        interested_in_special_needs: formData.role === 'parent' ? formData.interested_in_special_needs : false,
      });
      
      toast.success(localize({ ar: 'تم إنشاء الحساب بنجاح', en: 'Account created successfully' }));
      
      if (user.role === 'student') navigate('/student');
      else if (user.role === 'teacher') navigate('/teacher/profile');
      else if (user.role === 'parent') navigate('/parent');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail[language] || detail.ar : detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen gradient-hero flex items-center justify-center p-6 py-12 relative overflow-hidden ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img loading="lazy" src="/lwt-icon-new.png" alt="Learn With Trust" className="h-12 w-12 rounded-lg" />
            <div className="text-left">
              <span className="text-2xl font-bold text-emerald-600">Trust Ed Techno</span>
              <span className="block text-xs text-slate-500">ترست اي دي تكنو للتطوير والابتكار ذ.م.م</span>
            </div>
          </Link>
        </div>

        <Card className="border-0 shadow-emerald-hover">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-end mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-slate-600 hover:text-emerald-600"
              >
                <Globe className="w-4 h-4 mr-1" />
                {language === 'ar' ? 'English' : 'العربية'}
              </Button>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              {t('createAccount')}
            </CardTitle>
            <CardDescription className="text-slate-500">
              {localize({ ar: 'انضم إلى مجتمع Trust Ed Techno', en: 'Join the Trust Ed Techno community' })}
            </CardDescription>
            {/* Trial Badge */}
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm mt-2">
              <Shield className="w-3 h-3" />
              {t('freeTrial')}
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Parental responsibility notice — facilitation-tool positioning (Feb 2026 directive).
                Sits at the top of the form so it sets the legal expectation BEFORE any data is entered. */}
            <div
              className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-relaxed text-amber-900"
              role="note"
              data-testid="register-parental-notice"
            >
              <p className="font-bold mb-1 flex items-center gap-1.5">
                <span aria-hidden>ℹ️</span>
                {localize({ ar: 'تنويه قبل التسجيل', en: 'Before you sign up' })}
              </p>
              <p>
                {localize({
                  ar: 'تعمل Learn With Trust كأداة تيسيرية تربط أولياء الأمور بالمعلمين المستقلين. مسؤولية إنشاء الحسابات واختيار المعلم والمحتوى تقع كاملةً على ولي الأمر.',
                  en: 'Learn With Trust acts as a facilitation tool linking parents with independent tutors. Account creation, tutor selection, and content choices are the sole responsibility of the parent.'
                })}
              </p>
              <a
                href="/parent-responsibilities"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-amber-800 font-bold underline hover:text-amber-900 text-[12px]"
                data-testid="register-parental-link"
              >
                {localize({ ar: 'اقرأ مسؤوليات ولي الأمر كاملةً ↗', en: 'Read the full Parent Responsibilities ↗' })}
              </a>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Role Selection */}
              <div className="space-y-3">
                <Label className="text-slate-700">{t('registerAs')}</Label>
                <RadioGroup
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem 
                      value="student" 
                      id="student" 
                      className="peer sr-only" 
                      data-testid="register-role-student"
                    />
                    <Label
                      htmlFor="student"
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50 hover:border-emerald-300"
                    >
                      <BookOpen className="w-6 h-6 text-emerald-500" />
                      <span className="font-medium">{t('student')}</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem 
                      value="teacher" 
                      id="teacher" 
                      className="peer sr-only"
                      data-testid="register-role-teacher"
                    />
                    <Label
                      htmlFor="teacher"
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50 hover:border-emerald-300"
                    >
                      <Users className="w-6 h-6 text-emerald-500" />
                      <span className="font-medium">{t('teacher')}</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem 
                      value="parent" 
                      id="parent" 
                      className="peer sr-only"
                      data-testid="register-role-parent"
                    />
                    <Label
                      htmlFor="parent"
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all peer-data-[state=checked]:border-emerald-500 peer-data-[state=checked]:bg-emerald-50 hover:border-emerald-300"
                    >
                      <Shield className="w-6 h-6 text-emerald-500" />
                      <span className="font-medium">{localize({ ar: 'ولي أمر', en: 'Parent' })}</span>
                    </Label>
                  </div>
                </RadioGroup>

                {/* Autism-path opt-in — only shows when "Parent" is selected.
                    Adds zero friction for everyone else; gives families a
                    discrete, opt-in entry to specialised tutors. */}
                {formData.role === 'parent' && (
                  <label
                    className="mt-3 flex items-start gap-3 p-3 rounded-xl border-2 border-emerald-100 bg-emerald-50/60 cursor-pointer hover:bg-emerald-50 transition-colors"
                    data-testid="parent-autism-opt-in"
                  >
                    <input
                      type="checkbox"
                      checked={formData.interested_in_special_needs}
                      onChange={(e) => setFormData({ ...formData, interested_in_special_needs: e.target.checked })}
                      className="mt-1 w-4 h-4 accent-emerald-600"
                      data-testid="parent-autism-checkbox"
                    />
                    <span className="text-sm text-slate-700 leading-snug">
                      <span className="font-semibold block mb-0.5">
                        {localize({ ar: '🤝 مسار الدعم المتخصّص', en: '🤝 Specialized support path' })}
                      </span>
                      <span className="text-[12px] text-slate-600">
                        {localize({
                          ar: 'هل تود استكشاف مسار الدعم المخصّص لأطفال طيف التوحد والاحتياجات الخاصة؟',
                          en: 'Would you like to explore our specialised path for children on the autism spectrum or with special needs?'
                        })}
                      </span>
                    </span>
                  </label>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">{t('fullName')}</Label>
                <div className="relative">
                  <User className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="name"
                    type="text"
                    placeholder={localize({ ar: 'أدخل اسمك الكامل', en: 'Enter your full name' })}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500`}
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">{t('email')}</Label>
                <div className="relative">
                  <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500`}
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700">{t('phone')} ({localize({ ar: 'اختياري', en: 'optional' })})</Label>
                <div className="relative">
                  <Phone className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+971 XX XXX XXXX"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10' : 'pl-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500`}
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">{t('password')}</Label>
                <div className="relative">
                  <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500`}
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">{t('confirmPassword')}</Label>
                <div className="relative">
                  <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400`} />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500`}
                    required
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors`}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={formData.accepted_terms}
                  onCheckedChange={(checked) => setFormData({ ...formData, accepted_terms: checked })}
                  data-testid="accept-terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
                  {localize({ ar: 'أوافق على ', en: 'I accept the ' })}
                  <Link to="/terms" target="_blank" className="text-emerald-600 hover:underline">
                    {t('termsAndConditions')}
                  </Link>
                  {localize({ ar: ' وأفهم إخلاء مسؤولية المنصة', en: ' and understand the platform disclaimer' })}
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-lg"
                data-testid="register-submit-btn"
              >
                {loading ? (localize({ ar: 'جاري إنشاء الحساب...', en: 'Creating account...' })) : t('register')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                {t('alreadyHaveAccount')}{' '}
                <Link 
                  to="/login" 
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                  data-testid="login-link"
                >
                  {t('loginNow')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
            {localize({ ar: 'العودة للصفحة الرئيسية', en: 'Back to Home' })}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
